import React, { useCallback, useEffect, useRef, useState } from 'react';

// components
import Button from '../../../components/Button/Button';
import alert from '../../../helpers/alert';
import { colors } from '../../../constants/colors';

// api
import getAppRefresh from '../../../api/reports/getAppRefresh';
import startAppRefresh from '../../../api/reports/startAppRefresh';
import importAppInstalls from '../../../api/reports/importAppInstalls';

import { errorText, importLines, isBusy, refreshSummary, sourceRows } from './appRefresh';

const POLL_MS = 4000;

const styles = {
  card: { marginTop: 20, marginBottom: 20, padding: '30px 20px', backgroundColor: 'white', border: '1px solid #e0e0e0' },
  title: { fontWeight: 700, fontSize: 20, lineHeight: '20px', color: colors.armadillo, margin: 0 },
  hint: { color: colors.dimGray, fontSize: 14, lineHeight: '20px', margin: '8px 0 0' },
  row: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 20, marginTop: 20 },
  section: { marginTop: 28, paddingTop: 20, borderTop: '1px solid #e0e0e0' },
  sources: { margin: '12px 0 0', padding: 0, listStyle: 'none', lineHeight: '26px' },
  link: { color: 'var(--brand-primary)', fontWeight: 700 },
};
const tones = { ok: colors.green, error: colors.red, busy: colors.orange, muted: colors.dimGray };

// Обновление таблицы «Воронка приложения» в Google и загрузка выгрузок сторов.
// Кнопка лишь ставит заявку, обновление выполняет сервер в течение минуты; статус опрашиваем, пока оно не закончится.
const AppReportRefresh = () => {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState([]);
  const fileInput = useRef(null);
  const wasBusy = useRef(false);

  const load = useCallback(async () => {
    try {
      const res = await getAppRefresh();
      if (res?.data) setStatus(res.data);
      setError('');
    } catch (e) {
      setError(errorText(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const busy = isBusy(status);

  useEffect(() => {
    if (!busy) return undefined;
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [busy, load]);

  // Обновление закончилось (было в работе, а теперь нет) — сообщаем итог
  useEffect(() => {
    if (wasBusy.current && !busy && status) {
      if (status.ok) alert.success('Таблица обновлена');
      else alert.error(`Обновление не удалось: ${status.message || 'причина неизвестна'}`);
    }
    wasBusy.current = busy;
  }, [busy, status]);

  const start = async () => {
    setStarting(true);
    try {
      const res = await startAppRefresh();
      if (res?.data) setStatus(res.data);
      setError('');
    } catch (e) {
      setError(errorText(e));
    } finally {
      setStarting(false);
    }
  };

  const upload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    setUploading(true);
    setResults([]);
    try {
      const res = await importAppInstalls(formData);
      if (res?.data) {
        setStatus(res.data);
        setResults(importLines(res.data.files));
      }
      setError('');
    } catch (e) {
      setError(errorText(e));
    } finally {
      setUploading(false);
      // Тот же файл можно выбрать повторно
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const summary = refreshSummary(status);

  return (
    <div style={styles.card}>
      <p style={styles.title}>Таблица «Воронка приложения» в Google</p>
      <p style={styles.hint}>
        Полностью обновляется сама каждый день в {status?.schedule || '09:30'} (Алматы): установки, активность в
        приложении, воронка и сделки из приложения. Лист «Лиды» обновляется ещё и каждый час.
      </p>

      <div style={styles.row}>
        <Button fill text="Обновить таблицу" onClick={start} disabled={busy || starting || !status} />
        {status?.sheetUrl && (
          <a href={status.sheetUrl} target="_blank" rel="noreferrer" style={styles.link}>
            Открыть таблицу
          </a>
        )}
        <span style={{ color: tones[summary.tone] }}>{summary.text}</span>
      </div>
      {error && <p style={{ ...styles.hint, color: colors.red }}>{error}</p>}

      <div style={styles.section}>
        <p style={{ ...styles.title, fontSize: 16 }}>Данные сторов</p>
        <p style={styles.hint}>
          Сторы не отдают установки автоматически: выгрузите CSV из App Store Connect («Первые загрузки») и Google Play
          («Источники трафика», все страны) и загрузите сюда. Платформа определяется по файлу, после загрузки таблица
          обновится сама.
        </p>
        <ul style={styles.sources}>
          {sourceRows(status?.installs).map((row) => (
            <li key={row.title}>
              {row.title}: данные по {row.lastDay}
              {row.stale && <span style={{ color: colors.orange }}> — пора загрузить свежую выгрузку</span>}
            </li>
          ))}
        </ul>
        <div style={styles.row}>
          <input
            ref={fileInput}
            type="file"
            accept=".csv,text/csv"
            multiple
            onChange={upload}
            disabled={uploading}
            data-testid="stores-csv"
          />
          {uploading && <span style={{ color: colors.dimGray }}>Загружаем…</span>}
        </div>
        {results.length > 0 && (
          <ul style={styles.sources}>
            {results.map((r) => (
              <li key={r.text} style={{ color: r.ok ? colors.green : colors.red }}>
                {r.text}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AppReportRefresh;
